import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { notificationsAPI } from '../services/api';
import websocketService from '../services/websocket';

// DEC-1462: the consumer's "Finish your enrollment" card. A monoline checklist
// glyph (SVG in currentColor, house rule — never a new emoji) sized to the
// 24px box the emoji glyphs occupy in the card's leading span.
const ENROLLMENT_STEPS_PENDING = 'enrollment_steps_pending';
const StepsGlyph = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'block' }}
  >
    <path d="M4 6.5l1.5 1.5L8 5.5" />
    <path d="M11 6.5h9" />
    <path d="M4 12.5l1.5 1.5L8 11.5" />
    <path d="M11 12.5h9" />
    <circle cx="6" cy="18.5" r="1.6" />
    <path d="M11 18.5h9" />
  </svg>
);

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }, []);

  const handleNewNotification = useCallback((notification) => {
    setNotifications((prev) => [notification, ...prev]);
    setUnreadCount((prev) => prev + 1);
    // DEC-1462: site_id / site_name are joined server-side by GET
    // /notifications, so a socket-pushed row that carries neither a site nor
    // an explicit metadata.link would sit in the list as a dead non-link until
    // the dropdown reopens. Refetch so the pushed card gets its destination.
    const hasDestination = !!notification?.site_id || !!notification?.metadata?.link;
    if (!hasDestination) loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();

    // Listen for real-time notifications via WebSocket
    websocketService.on('new_notification', handleNewNotification);

    // Fallback poll every 60 seconds in case WebSocket drops
    const interval = setInterval(() => {
      loadUnreadCount();
    }, 60000);

    return () => {
      websocketService.off('new_notification', handleNewNotification);
      clearInterval(interval);
    };
  }, [handleNewNotification, loadNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      loadNotifications();
    }
  };

  // Notifications that resolve to a site (via metadata.siteId for program
  // matches, or via the related enrollment's site_id for enrollment_*
  // events) navigate to that site's detail page on click. Notifications
  // without a resolvable site stay as plain "mark as read" actions —
  // there's no destination to send the user to. The dropdown closes on
  // navigation so the destination page isn't covered by a hovering panel.
  const handleNotificationClick = async (notification, opts = {}) => {
    if (opts.willNavigate) setShowDropdown(false);
    if (!notification.is_read) {
      try {
        await notificationsAPI.markAsRead(notification.id);
        setNotifications(notifications.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        ));
        setUnreadCount(Math.max(0, unreadCount - 1));
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'enrollment_request':
        return '📩';
      case 'enrollment_approved':
        return '✅';
      case 'enrollment_rejected':
        return '❌';
      case 'enrollment_pending':
        return '⏳';
      case 'event_created':
        return '📅';
      case 'payment_received':
        return '💰';
      case ENROLLMENT_STEPS_PENDING:
        return <StepsGlyph />;
      default:
        return '🔔';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        onClick={handleBellClick}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-haspopup="true"
        aria-expanded={showDropdown}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '24px',
          padding: '8px',
          color: 'var(--navy)',
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span aria-live="polite" style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: 'var(--danger, red)',
            color: 'white',
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '600',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '8px',
          /* DEC-30: Fixed 400px overflows the viewport on narrow phones.
             maxWidth caps the panel on large screens while width: calc(100vw - 32px)
             provides 16px gutter on each side on any viewport narrower than 432px.
             right: 0 (already set above) anchors the panel flush to the bell icon
             so it never bleeds off the right edge regardless of screen width. */
          maxWidth: '400px',
          width: 'calc(100vw - 32px)',
          maxHeight: '500px',
          background: 'white',
          border: '1px solid var(--medium-gray)',
          borderRadius: 'var(--border-radius)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px',
            borderBottom: '1px solid var(--medium-gray)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--teal)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                }}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔕</div>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => {
                // Server-resolved (notificationsController). Present when
                // the recipient owns a site the notification is "about" —
                // see the controller's leading comment for the source-path
                // mapping. When set, the entire card is a Link to the
                // site detail page (still marks the notification read).
                const siteId = notification.site_id;
                const siteName = notification.site_name;
                // DEC-980: some notifications are about the ACCOUNT, not a
                // site — "you've earned $X, connect a bank" has no site to open
                // and would otherwise render as a card that promises money and
                // does nothing when tapped. `metadata.link` carries an explicit
                // in-app destination for those. Restricted to a root-relative
                // path with no protocol, so a notification row can never
                // redirect a user off-site.
                const metaLink = notification.metadata?.link;
                const accountLink =
                  typeof metaLink === 'string' && /^\/[A-Za-z0-9\-_/?=&.]*$/.test(metaLink)
                    ? metaLink
                    : null;
                const isLink = !!siteId || !!accountLink;
                // Device-scoped notifications (DEC-1061 gap-fill) carry the
                // device id in metadata; forwarding it as SiteDetails'
                // existing `?device=` param (DEC-1016 R3) opens the exact
                // edit-device modal instead of the bare site page. The base
                // path still comes from the server-resolved (ownership-
                // checked) site_id — metadata only contributes the param,
                // and only when it's a well-formed UUID.
                const metaDeviceId = notification.metadata?.deviceId;
                const deviceParam =
                  siteId && typeof metaDeviceId === 'string' &&
                  /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(metaDeviceId)
                    ? `?device=${metaDeviceId}`
                    : '';
                // DEC-1454: the DEC-1462 steps card carries metadata.taskId
                // ONLY while exactly one required task remains (founder ruling
                // 2026-10-06; the server removes the key otherwise). Forwarded
                // as SiteDetails' existing ?task= deep-link (scroll, pulse,
                // auto-open). Same UUID guard as deviceId; a device param wins
                // if both ever appear (they never co-occur today).
                const metaTaskId = notification.metadata?.taskId;
                const taskParam =
                  siteId && !deviceParam && typeof metaTaskId === 'string' &&
                  /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(metaTaskId)
                    ? `?task=${metaTaskId}`
                    : '';
                // A server-resolved (ownership-checked) site always wins over a
                // metadata-supplied path.
                const linkTo = siteId ? `/sites/${siteId}${deviceParam || taskParam}` : accountLink;

                const cardStyle = {
                  padding: '16px',
                  borderBottom: '1px solid var(--light-gray)',
                  cursor: 'pointer',
                  background: notification.is_read ? 'white' : 'rgba(32, 178, 170, 0.05)',
                  transition: 'background 0.2s',
                  border: 'none',
                  width: '100%',
                  textAlign: 'left',
                  display: 'block',
                  textDecoration: 'none',
                  color: 'inherit',
                };
                const cardHandlers = {
                  onMouseEnter: (e) => e.currentTarget.style.background = 'var(--light-gray)',
                  onMouseLeave: (e) => e.currentTarget.style.background = notification.is_read ? 'white' : 'rgba(32, 178, 170, 0.05)',
                  onFocus: (e) => e.currentTarget.style.background = 'var(--light-gray)',
                  onBlur: (e) => e.currentTarget.style.background = notification.is_read ? 'white' : 'rgba(32, 178, 170, 0.05)',
                };
                const ariaLabel = `${notification.is_read ? '' : 'Unread: '}${notification.title}`
                  + (siteId ? `. Opens ${siteName || 'site details'}.` : '')
                  + (!siteId && accountLink ? '. Opens your dashboard.' : '');

                const inner = (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <span style={{ fontSize: '24px', flexShrink: 0 }}>
                      {getNotificationIcon(notification.notification_type || notification.type)}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: notification.is_read ? '400' : '600',
                        marginBottom: '4px',
                        fontSize: '14px',
                      }}>
                        {notification.title}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                        marginBottom: '8px',
                      }}>
                        {notification.message}
                      </div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {formatTime(notification.created_at)}
                        </span>
                        {isLink && (
                          <span style={{
                            fontSize: '12px',
                            color: 'var(--teal)',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                          }}>
                            {/* DEC-1454: one verb for the one-left action, on
                                every surface (email CTA, tile button). */}
                            {deviceParam ? 'View device →' : taskParam ? 'Finish this step →' : 'View site →'}
                          </span>
                        )}
                      </div>
                    </div>
                    {!notification.is_read && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'var(--teal)',
                        flexShrink: 0,
                        marginTop: '6px',
                      }} />
                    )}
                  </div>
                );

                return isLink ? (
                  <Link
                    key={notification.id}
                    to={linkTo}
                    onClick={() => handleNotificationClick(notification, { willNavigate: true })}
                    aria-label={ariaLabel}
                    style={cardStyle}
                    {...cardHandlers}
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    aria-label={ariaLabel}
                    style={cardStyle}
                    {...cardHandlers}
                  >
                    {inner}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
